import { FC, useEffect, useRef, useState } from 'react'
import { useLayout } from '../../../_metronic/layout/core'
import SVG from 'react-inlinesvg'
import { Link, useHistory } from 'react-router-dom'
import { useSelector, shallowEqual } from 'react-redux'
import ReactApexChart from 'react-apexcharts'
import { getAllMemberships } from '../../services/membershipService'
import { RootState } from '../../../setup'
import Pagination from '../components/Pagination'
import { LoyaltyCredit, LoyaltyCreditStats, SubscriptionStatus } from './models'
import dayjs from 'dayjs'
import { formatCurrencyUSD } from '../../helpers/FormatCurrency'
import { getLoyaltyProgramsActivities, getMyCredits } from '../../services/loyaltyProgramService'
import { CreditItem } from '../memberships/components/MembershipCreditsWidget'
import { useConfirmAction } from '../../hooks/utils/useConfirmAction'
import { cancelMembership, statusMembership } from '../../services/subscriptionService'
import { formatDateShortIntl2 } from '../../helpers/FormatDate'
import { showErrorAlert } from '../../helpers/alert'
import { LoyaltySummary } from '../profileMembers/components/creditsAndPayments/CreditsAndPaymentsWrapper'

const MembershipsFinancialPage: FC = () => {
    const { config, setLayout } = useLayout()
    const restoreRef = useRef(config)
    const history = useHistory()
    const [activities, setActivities] = useState<CreditItem[]>([])
    const [memberships, setMemberships] = useState<any[]>([])
    const [membershipUpgrade, setMembershipUpgrade] = useState<any | null>(null)
    const [currentMembership, setCurrentMembership] = useState<any | null>(null)
    const [subscription, setCurrentSubscription] = useState<any | null>(null)
    const [receipts, setReceipts] = useState<LoyaltyCredit[]>([]);
    const [stats, setStats] = useState<LoyaltySummary>({});
    const [currentPage, setCurrentPage] = useState(1)
    const [count, setCount] = useState<number>(0);
    const limit = 10;
    const totalPages = count
        ? Math.ceil(count / limit)
        : 1;
    const confirm = useConfirmAction()
    const [currentMembershipStatus, setCurrentMembershipStatus] = useState<SubscriptionStatus>({})

    const expiringText = `${stats?.expiringSoon?.credits || 0} credits is expiring on ${stats?.expiringSoon?.endDate ? formatDateShortIntl2(stats?.expiringSoon?.endDate) : ''}`

    useEffect(() => {
        restoreRef.current = config
        setLayout({
            ...config,
            header: { ...config.header, display: true },
            aside: { ...config.aside, display: true },
            toolbar: { ...config.toolbar, display: false },
            footer: { ...config.footer, display: true },
        })
        getActivities()
        getCredits(currentPage)
        return () => setLayout(restoreRef.current)
    }, [])

    useEffect(() => {
        const fetchMemberships = async () => {
            const mems = await getAllMemberships()
            const data = mems.data

            const currentPlanTitle = (subscription?.plan?.title || '').toLowerCase().trim();
            const isHighTier = currentPlanTitle.includes('founding') || currentPlanTitle.includes('electi+') || currentPlanTitle.includes('plus');

            const current = data.find((m) => m.id === subscription?.plan?.id)
            setCurrentMembership(current)

            if (isHighTier) {
                // Si es Founding Member* o Electi+, no debe aparecer el botón de upgrade
                setMembershipUpgrade(null);
            } else {
                // Si es Electi (nivel básico), buscar el plan Electi+ para el upgrade
                const electiPlus = data.find((m: any) => m.title?.toLowerCase().includes('electi+') || m.title?.toLowerCase().includes('plus'));
                setMembershipUpgrade(electiPlus || null);
            }
        }

        getStatusMembership()
        fetchMemberships()
    }, [subscription])

    
    const getStatusMembership = async () => {
        const status = await statusMembership();
        setCurrentMembershipStatus(status);
    }

    const getActivities = async () => {
        let rows = await getLoyaltyProgramsActivities();
        setActivities(rows);
    }

    const getCredits = async (page: number) => {
        const offset = (page - 1) * limit;
        // Member's own loyalty credits + stats + membership from the ledger read API
        // (member/transactions/); LimitOffset paginated, no user_id → self.
        const credits = await getMyCredits({ limit, offset })
        setCount(credits?.count || 0)
        setReceipts(credits?.results || [])
        setStats(credits?.stats || {})
        setCurrentSubscription(credits?.membership || {})
    }

    const chartOptions = {
        chart: { type: 'donut' },
        labels: ['Earnings', 'Expire', 'redemptions'],
        colors: ['#540008', '#ccc', '#eee'],
        legend: { show: false },
        dataLabels: { enabled: false },
    }

    const chartSeries = [stats?.totalEarnings || 0, stats?.totalExpiration || 0, stats?.totalRedemptions || 0]

    const handlePageChange = async (page: number) => {
        setCurrentPage(page);
        await getCredits(page);
    }

    const accessLabel = (() => {
        const title = (currentMembership?.title || '').toLowerCase().trim();
        if (title.includes('electi+') || title.includes('plus') || title.includes('founding')) {
            return 'Full Access';
        }
        return 'Basic Access';
    })();

    const handleOnCancelMembership = async () => {
        let isConfirm = await confirm({ title: `Confirm ${currentMembership?.title} Cancellation`, message: `Your ${currentMembership?.title} (${accessLabel}) will conclude on ${currentMembershipStatus?.currentPeriodEnd ? formatDateShortIntl2(currentMembershipStatus?.currentPeriodEnd) : 'Not date defined'}.`, btnTextDecline: `Keep ${currentMembership?.title}`, btnTextAction: `Confirm cancellation` });// From then, your membership will continue under Electi (Basic Access).
        if(isConfirm){
            let cancelResponse = await cancelMembership({});
            showErrorAlert({ title: 'Membership Cancellation', message: cancelResponse.message || 'Your membership has been cancelled successfully.' });
            getStatusMembership();
        }
    }

    return (
        <div className="financial-page">
            <div className="financial-membership">
                <div className="financial-membership__container">

                    {/* HEADER */}
                    <div className="financial-membership__header">
                        <h1 className="financial-membership__title">My Membership</h1>

                        <Link to="/terms-conditions" className="financial-membership__link">
                            Membership and Loyalty Program Terms & Conditions
                        </Link>
                    </div>

                    {/* MEMBERSHIP */}
                    <div className="financial-membership__section">
                        <span className="financial-membership__label">Membership</span>

                        <div className="financial-membership__card mt-4">
                            <div className="financial-membership__card-top">
                                <div>
                                    <div className="financial-membership__plan-title">{currentMembership?.title || '-'}</div>
                                    <div className="financial-membership__plan-description">{accessLabel}</div>
                                </div>

                                {membershipUpgrade && (
                                    <button className="financial-membership__upgrade cursor-pointer" onClick={()=>history.push('/membership/change')}>
                                        <span>Upgrade to {membershipUpgrade.title}</span><br />
                                        <small>{membershipUpgrade.priceStr}</small>
                                    </button>
                                )}
                            </div>

                            <div className="financial-membership__features mt-4">
                                {/* CURRENT */}
                                {membershipUpgrade && currentMembership && (
                                    <div className="financial-membership__features-basic">
                                        <span className="title">YOUR PLAN LETS YOU:</span>
                                        <ul>
                                            {currentMembership.features.map((f: string, i: number) => (
                                                <li key={i}>
                                                    <SVG src="/media/svg/nobilis/check-black.svg" />
                                                    &nbsp;&nbsp;{f}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* UPGRADE */}
                                {(membershipUpgrade || currentMembership) && (
                                    <div className="financial-membership__features-pro">
                                        <span className="title">
                                            {membershipUpgrade
                                                ? `UPGRADE TO ${membershipUpgrade.title.toUpperCase()} TO:`
                                                : 'YOUR PLAN LETS YOU:'}
                                        </span>

                                        <ul>
                                            {(membershipUpgrade || currentMembership)?.features.map(
                                                (f: string, i: number) => (
                                                    <li key={i}>
                                                        <SVG src="/media/svg/nobilis/check-white.svg" />
                                                        &nbsp;&nbsp;{f}
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* LOYALTY */}
                    <div className="financial-membership__section">
                        <span className="financial-membership__label">
                            Loyalty program
                        </span>

                        <p className="financial-membership__description">
                            Our loyalty program rewards active participation with credits
                        </p>

                        <div className="financial-membership__card">
                            <span className="financial-membership__subcard-title">
                                Membership & Loyalty
                            </span>

                            <div className="financial-membership__loyalty-content">

                                {/* LEFT */}
                                <div className="left">
                                    <div className="financial-membership__credits">
                                        <ReactApexChart
                                            options={chartOptions}
                                            series={chartSeries}
                                            type="donut"
                                            height={220}
                                        />

                                        <div className="center-text">
                                            <span className="in-graphic-label">Saved</span>
                                            <strong className="in-graphic-number">{formatCurrencyUSD((stats?.totalCredits || 0),0)}</strong>
                                            <span>
                                                <img src="/media/laurel-wreath.png" height={27} width={27} alt="" />
                                            </span>
                                        </div>
                                    </div>

                                    <div className="financial-membership__info">
                                        <div className="credits-info">
                                            <div className="credits-info__title">
                                                1 Credit = 1 USD <br />
                                                1 Year {currentMembership?.title} Membership Price - ${currentMembership?.price}
                                            </div>

                                            <div className="credits-info__description">
                                                Credits can fully cover the cost of an {currentMembership?.title} Membership <br />
                                                for up to 3 years (75.000 Credits). <br />
                                                Any remaining balance may be paid in cash.
                                            </div>

                                            <div className="membership-contribution">
                                                <div className="membership-contribution__left">
                                                    <div className="membership-contribution__label">
                                                    Your {currentMembership?.title} Membership Contribution
                                                    </div>
                                                </div>

                                                <div className="membership-contribution__right">
                                                    <div className="membership-contribution__amount">
                                                    $ {currentMembership?.price}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className='financial-membership__cancel-subscription' onClick={handleOnCancelMembership}>
                                                Cancel {currentMembership?.title}
                                            </div>

                                            <div className="credits-expiring">
                                                <div className="credits-expiring__text">
                                                {expiringText}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* RIGHT */}
                                <div className="right">
                                    <div className="financial-membership__history">
                                        <div className="financial-membership__row_header">
                                            <span>Date</span>
                                            <span>Description</span>
                                            <span>Credits</span>
                                        </div>

                                        {(receipts || []).map((rep, i) => (
                                            <div key={i} className="financial-membership__row">
                                                <span>{formatDateShortIntl2(rep.date)}</span>
                                                <span>{rep.description}</span>
                                                <span>{rep.credit}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className='tap-flex-center w-100'>
                                        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* ACTIVITY */}
            <div className="financial-activity">
                <div className="financial-activity__card">

                    <div className="financial-activity__header p-3">
                        <div>Activity</div>
                        <div>Credit Earning Rate</div>
                        <div>Notes</div>
                    </div>

                    {activities.map((row, i) => (
                        <div key={i} className={`financial-activity__row `}>
                            <div>{row.activity}</div>
                            <div className="multiline">{row.creditEarningRate}</div>
                            <div>{row.notes}</div>
                        </div>
                    ))}

                </div>
            </div>
        </div>
    )
}

export { MembershipsFinancialPage }